import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import dayjs from 'dayjs';
import { useState, useMemo } from 'react';
import { styled } from 'styled-components';
import { useQueryRecapForDate } from '../queries/useQueryRecapForDate.js';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { Button } from '@fabric-app/ui/components/Button';
import { TypographyContainer, P, UL, LI, SPAN, A } from '@fabric-app/ui/components/Typography';
import { Modal } from '@fabric-app/ui/components/Modal/Modal';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { Flex } from '@fabric-app/ui/components/Flex';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { ErrorMessages } from '../../errors/errors.messages.js';
import { getErrorType } from '../../errors/errors.utils.js';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { useExpandedResource, useShowExpandedResource } from '../../resources/stores/useExpandedResourceStore.js';
import { Link } from '@fabric-app/compat';
import { useFeatureGuard_recaps } from '../../pricing/hooks/useFeatureGuard.js';

const StyledBody = styled(Modal.Body).withConfig({
  displayName: "RecapSummaryModal__StyledBody",
  componentId: "sc-8363ce1e-0"
})([
  `padding:50px;min-height:100%;`,
  `{padding:16px;color:`,
  `;}`
], mediaMobile, cssVar["color-text-tertiary"]);
const SetupButton = styled(Button).attrs({
  variant: "bg-primary-reverse",
  size: "xs"
}).withConfig({
  displayName: "RecapSummaryModal__SetupButton",
  componentId: "sc-8363ce1e-1"
})([
  `white-space:nowrap;font-weight:500;`
]);
const RecapSummaryModal = ({ date: dateProp, children, field }) => {
  const { isMobileView } = useResponsive();
  const [open, setOpen] = useState(false);
  const recapGuard = useFeatureGuard_recaps();
  const date = dayjs(dateProp).format("dddd, MMMM Do");
  const { data, isLoading, error } = useQueryRecapForDate({
    date: dateProp,
    field
  }, {
    enabled: open
  });
  const errorType = getErrorType(error);
  const expandedResourceId = useExpandedResource();
  const showExpandedResource = useShowExpandedResource();
  const onOpenChange = (isOpen) => {
    if (expandedResourceId && !isOpen) {
      return;
    }
    setOpen(isOpen);
  };
  const memoizedChildren = useMemo(() => children, [
    children
  ]);
  return /* @__PURE__ */ jsxs(Modal.Root, {
    open,
    onOpenChange,
    children: [
      /* @__PURE__ */ jsx(Modal.Trigger, {
        asChild: true,
        children: memoizedChildren
      }),
      /* @__PURE__ */ jsxs(Modal.Portal, {
        children: [
          /* @__PURE__ */ jsx(Modal.Overlay, {}),
          /* @__PURE__ */ jsxs(Modal.Content, {
            onInteractOutside: (e) => expandedResourceId && e.preventDefault(),
            gap: "none",
            width: "lg",
            dynamicHeight: false,
            children: [
              /* @__PURE__ */ jsxs(Modal.BaseHeader, {
                padding: "sm",
                mobileShadowOverContent: true,
                children: [
                  /* @__PURE__ */ jsx(Modal.Title, {
                    size: "sm",
                    children: isMobileView ? `${date} summary` : `Your summary for ${date}`
                  }),
                  /* @__PURE__ */ jsx(Modal.Close, {})
                ]
              }),
              /* @__PURE__ */ jsx(ScrollArea.Default, {
                grow: true,
                expandVertically: true,
                children: /* @__PURE__ */ jsxs(StyledBody, {
                  children: [
                    isLoading && /* @__PURE__ */ jsx(Flex, {
                      direction: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      flexGrow: true,
                      children: /* @__PURE__ */ jsx(Spinner, {
                        size: 32,
                        thickness: 4
                      })
                    }),
                    data?.resources && !isLoading && /* @__PURE__ */ jsxs(TypographyContainer, {
                      gap: "elementsContainer",
                      children: [
                        /* @__PURE__ */ jsx(P, {
                          color: "primary",
                          size: "md",
                          children: data?.overallSummary
                        }),
                        /* @__PURE__ */ jsx(P, {
                          color: "primary",
                          size: "md",
                          weight: 700,
                          children: "Top highlights:"
                        }),
                        /* @__PURE__ */ jsx(UL, {
                          children: data?.resources?.map((item) => /* @__PURE__ */ jsxs(LI, {
                            children: [
                              item.title && /* @__PURE__ */ jsxs(Fragment, {
                                children: [
                                  /* @__PURE__ */ jsx(SPAN, {
                                    weight: 700,
                                    color: "primary",
                                    children: /* @__PURE__ */ jsx(A, {
                                      onClick: () => {
                                        showExpandedResource(item.resourceId);
                                      },
                                      children: item.title
                                    })
                                  }),
                                  " - "
                                ]
                              }),
                              /* @__PURE__ */ jsx(SPAN, {
                                color: "primary",
                                children: item.summary
                              })
                            ]
                          }, item.resourceId))
                        })
                      ]
                    }),
                    (error || !data?.resources) && !isLoading && /* @__PURE__ */ jsx(P, {
                      color: "tertiary",
                      size: "md",
                      children: errorType ? ErrorMessages[errorType] : "Unable to load the summary. Please try again later."
                    })
                  ]
                })
              }),
              /* @__PURE__ */ jsxs(Modal.Footer, {
                variant: "call-to-action",
                children: [
                  /* @__PURE__ */ jsx(P, {
                    size: "sm",
                    color: "primary",
                    weight: 500,
                    children: isMobileView ? "Get email summaries of the notes and content you’ve saved." : "Get daily, weekly, or monthly email summaries of the notes and content you’ve saved."
                  }),
                  /* @__PURE__ */ jsx(SetupButton, {
                    as: Link,
                    onClick: (e) => {
                      const { isAllowed } = recapGuard.check(void 0, {
                        showUpsellBannerOnNotAllowed: true
                      });
                      if (!isAllowed) {
                        e.preventDefault();
                      }
                    },
                    href: "/settings/recap",
                    children: "Set up Recaps"
                  })
                ]
              })
            ]
          })
        ]
      })
    ]
  });
};

export { RecapSummaryModal as default };
//# sourceMappingURL=RecapSummaryModal.js.map
